import React, { useState, useEffect } from 'react';
import { adminApi } from '../api/apiClient';
import './AdminDashboard.css';

export default function AdminDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [savingRoleFor, setSavingRoleFor] = useState(null);
  const [usersError, setUsersError] = useState('');
  const [purgingLegal, setPurgingLegal] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [purgeError, setPurgeError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [dashRes, auditRes, usersRes] = await Promise.all([
        adminApi.dashboard(),
        adminApi.auditLog(0, 30),
        adminApi.users(),
      ]);
      setDashboard(dashRes.data);
      setAuditLog(auditRes.data.content || []);
      setUsers(usersRes.data || []);
    } catch (err) {
      console.error('Admin dashboard failed', err);
    } finally {
      setLoading(false);
    }
  };

  const changeRole = async (userId, role) => {
    setUsersError('');
    setSavingRoleFor(userId);
    try {
      await adminApi.setUserRole(userId, role);
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role } : u)));
    } catch (err) {
      setUsersError(err?.response?.data?.message || 'Could not update user role.');
    } finally {
      setSavingRoleFor(null);
    }
  };

  const purgeMockLegalData = async () => {
    const confirmed = window.confirm('This will permanently delete mock legal records from the database. Continue?');
    if (!confirmed) return;

    setPurgingLegal(true);
    setPurgeError('');
    setPurgeResult(null);
    try {
      const res = await adminApi.purgeMockLegalData();
      setPurgeResult(res?.data || null);
    } catch (err) {
      setPurgeError(err?.response?.data?.message || 'Could not purge mock legal data.');
    } finally {
      setPurgingLegal(false);
    }
  };

  if (loading) return <div className="v-loading">Loading admin dashboard...</div>;

  const totalDocuments = Number(dashboard?.totalDocuments ?? 0);
  const processingDocuments = Number(dashboard?.processingDocuments ?? 0);
  const securityScore = Number(dashboard?.securityScore ?? 78);
  const compliancePercent = Number(dashboard?.compliancePercent ?? 95);
  const failedQuestions = Number(dashboard?.failedQuestions ?? 0);
  const governanceStatus = failedQuestions > 0 ? 'ATTENTION' : 'HEALTHY';

  return (
    <div className="v-page">
      <div className="v-page__header">
        <div>
          <h1 className="v-page__title">AI Document Governance Console</h1>
          <p className="v-page__subtitle">Enterprise governance with Copilot-controlled execution</p>
        </div>
      </div>

      <section className="v-governance-hero" aria-label="Governance dashboard">
        <h2 className="v-governance-hero__title">Governance Dashboard</h2>
        <p className="v-governance-hero__subtitle">
          Cross-domain health for documents, security, and AI governance
        </p>
        <div className="v-governance-nav" role="list" aria-label="Governance sections">
          <span className="v-governance-nav__item" role="listitem">Documents</span>
          <span className="v-governance-nav__item" role="listitem">Security &amp; Compliance</span>
          <span className="v-governance-nav__item" role="listitem">AI Governance</span>
          <span className="v-governance-nav__item" role="listitem">Copilot</span>
        </div>

        <div className="v-gov-kpi-grid">
          <div className="v-gov-kpi-card">
            <span className="v-gov-kpi-card__label">Documents</span>
            <span className="v-gov-kpi-card__value">{totalDocuments}</span>
          </div>
          <div className="v-gov-kpi-card">
            <span className="v-gov-kpi-card__label">Processing</span>
            <span className="v-gov-kpi-card__value">{processingDocuments}</span>
          </div>
          <div className="v-gov-kpi-card">
            <span className="v-gov-kpi-card__label">Security score</span>
            <span className="v-gov-kpi-card__value">{securityScore}</span>
          </div>
          <div className="v-gov-kpi-card">
            <span className="v-gov-kpi-card__label">Compliance</span>
            <span className="v-gov-kpi-card__value">{compliancePercent}%</span>
          </div>
        </div>

        <p className="v-gov-health-line">
          Governance: {governanceStatus} · AI: HEALTHY · Security: No critical issues
        </p>

        <div className="v-admin-feature-table-wrap">
          <h3 className="v-admin-feature-table__title">Feature table</h3>
          <p className="v-admin-section__hint">
            Quick map of the AI Governance and Copilot capabilities exposed from the admin area.
          </p>
          <table className="v-table v-admin-feature-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Purpose</th>
                <th>Entry point</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>AI Governance dashboard</td>
                <td>Review document, compliance, and Q&amp;A health</td>
                <td>/admin</td>
                <td><span className="v-badge v-badge--neutral">Active</span></td>
              </tr>
              <tr>
                <td>Copilot diagnostics</td>
                <td>Inspect API logs, auth flow, and backend responses</td>
                <td>/admin/api-diagnostics</td>
                <td><span className="v-badge v-badge--neutral">Active</span></td>
              </tr>
              <tr>
                <td>Audit log review</td>
                <td>Track admin actions and governance changes</td>
                <td>Admin &gt; Audit Log</td>
                <td><span className="v-badge v-badge--neutral">Active</span></td>
              </tr>
              <tr>
                <td>User role management</td>
                <td>Promote users to ADMIN to manage the platform</td>
                <td>Admin &gt; Users</td>
                <td><span className="v-badge v-badge--neutral">Active</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Tabs */}
      <div className="v-tabs">
        <button className={`v-tab ${tab === 'overview' ? 'v-tab--active' : ''}`} onClick={() => setTab('overview')}>
          Overview
        </button>
        <button className={`v-tab ${tab === 'audit' ? 'v-tab--active' : ''}`} onClick={() => setTab('audit')}>
          Audit Log
        </button>
        <button className={`v-tab ${tab === 'users' ? 'v-tab--active' : ''}`} onClick={() => setTab('users')}>
          Users
        </button>
      </div>

      {/* Overview */}
      {tab === 'overview' && dashboard && (
        <>
          <div className="v-stats-row">
            <div className="v-stat-card">
              <span className="v-stat-card__value">{dashboard.totalUsers}</span>
              <span className="v-stat-card__label">Users</span>
            </div>
            <div className="v-stat-card">
              <span className="v-stat-card__value">{dashboard.totalWorkspaces}</span>
              <span className="v-stat-card__label">Workspaces</span>
            </div>
            <div className="v-stat-card">
              <span className="v-stat-card__value">{dashboard.totalDocuments}</span>
              <span className="v-stat-card__label">Documents</span>
            </div>
            <div className="v-stat-card">
              <span className="v-stat-card__value">{dashboard.totalQuestions}</span>
              <span className="v-stat-card__label">Questions</span>
            </div>
            <div className="v-stat-card">
              <span className="v-stat-card__value">{dashboard.failedQuestions ?? 0}</span>
              <span className="v-stat-card__label">Failed Q&amp;A</span>
            </div>
            <div className="v-stat-card">
              <span className="v-stat-card__value">{dashboard.qaInteractionsLast7Days ?? 0}</span>
              <span className="v-stat-card__label">Q&amp;A logs (7d)</span>
            </div>
            <div className="v-stat-card">
              <span className="v-stat-card__value">{dashboard.lowEvidenceInteractionsLast7Days ?? 0}</span>
              <span className="v-stat-card__label">Low-evidence (7d)</span>
            </div>
          </div>

          {dashboard.recentQaInteractions && dashboard.recentQaInteractions.length > 0 && (
            <div className="v-admin-section">
              <h3>Recent Q&amp;A retrieval logs</h3>
              <p className="v-admin-section__hint">Preview only; full question text is not stored.</p>
              <table className="v-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Workspace</th>
                    <th>Preview</th>
                    <th>Chunks</th>
                    <th>Top score</th>
                    <th>Low evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.recentQaInteractions.map((row) => (
                    <tr key={row.id}>
                      <td>{new Date(row.createdAt).toLocaleString()}</td>
                      <td>{row.workspaceId}</td>
                      <td>{row.privateMasked ? '*** (private; owner approval required)' : (row.questionPreview || '—')}</td>
                      <td>{row.chunkCount ?? '—'}</td>
                      <td>{row.topRetrievalScore != null ? Number(row.topRetrievalScore).toFixed(3) : '—'}</td>
                      <td>{row.lowEvidence ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Weekly activity */}
          {dashboard.weeklyActivity && Object.keys(dashboard.weeklyActivity).length > 0 && (
            <div className="v-admin-section">
              <h3>Weekly Activity</h3>
              <div className="v-activity-list">
                {Object.entries(dashboard.weeklyActivity).map(([action, count]) => (
                  <div key={action} className="v-activity-item">
                    <span className="v-activity-item__action">{action.replace(/_/g, ' ')}</span>
                    <span className="v-activity-item__count">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="v-admin-section v-admin-danger-zone">
            <h3>Legal Data Maintenance</h3>
            <p className="v-admin-section__hint">
              Remove legacy mock legal rows so only official-source legal data remains visible.
            </p>
            <button
              type="button"
              className="v-btn v-btn--ghost v-btn--sm v-admin-danger-btn"
              onClick={purgeMockLegalData}
              disabled={purgingLegal}
            >
              {purgingLegal ? 'Purging...' : 'Purge mock legal data'}
            </button>
            {purgeError && <p className="v-admin-error">{purgeError}</p>}
            {purgeResult && (
              <div className="v-admin-purge-result">
                <div>Status: {purgeResult.status || 'UNKNOWN'}</div>
                <div>Sources matched: {purgeResult.sourcesMatched ?? 0}</div>
                <div>Sources removed: {purgeResult.sourcesRemoved ?? 0}</div>
                <div>Versions removed: {purgeResult.versionsRemoved ?? 0}</div>
                <div>Fragments removed: {purgeResult.fragmentsRemoved ?? 0}</div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Audit Log */}
      {tab === 'audit' && (
        <div className="v-admin-section">
          <table className="v-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Action</th>
                <th>Resource</th>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              {auditLog.map((entry) => (
                <tr key={entry.id}>
                  <td>{new Date(entry.createdAt).toLocaleString()}</td>
                  <td>{entry.userEmail || `User #${entry.userId}`}</td>
                  <td><span className="v-badge v-badge--neutral">{entry.action}</span></td>
                  <td>{entry.resourceType}</td>
                  <td>{entry.resourceName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'users' && (
        <div className="v-admin-section">
          <h3>User Access</h3>
          <p className="v-admin-section__hint">Promote users to ADMIN so they can access admin pages and manage other users.</p>
          {usersError && <p className="v-admin-error">{usersError}</p>}
          <table className="v-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.fullName || '—'}</td>
                  <td>
                    <select
                      className="v-admin-role-select"
                      value={u.role || 'USER'}
                      disabled={savingRoleFor === u.id}
                      onChange={(e) => changeRole(u.id, e.target.value)}
                    >
                      <option value="USER">USER</option>
                      <option value="ADMIN">ADMIN</option>
                    </select>
                  </td>
                  <td>{u.active ? 'Active' : 'Inactive'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
